export type StratumKey = 'cognitive' | 'relational' | 'somatic' | 'anticipatory'

export interface Stratum {
  key: StratumKey
  label: string
  value: number // 0-10 contribution to felt load
}

export interface CareLoadState {
  total: number // 0-10 authoritative felt load (user sets this primarily)
  strata: Stratum[]
  diffuse: boolean // when user says "it doesn't split cleanly"
}

export interface FieldResponse {
  fieldKey: string
  value: any
  uncertainty: 0 | 1
  ts: string
}

export interface SessionState {
  id: string
  createdAt: string
  fieldNotes: string
  load: CareLoadState
  responses: Record<string, FieldResponse>
  uncertaintyTotal: number
}

export interface Receipt {
  id: string
  sessionId: string
  issuedAt: string
  careLoad: number
  careLoadLabel: string
  fragmentationCount: number
  uncertaintyCount: number
  readingText: string
  snapshot: SessionState
  privateCode?: string
}

export const STRATA_DEFS: { key: StratumKey; label: string; desc: string }[] = [
  { key: 'cognitive', label: 'Cognitive', desc: 'planning, weighing, internal scripting' },
  { key: 'relational', label: 'Relational', desc: 'relationship temperature, imagined judgment, belonging' },
  { key: 'somatic', label: 'Somatic', desc: 'body cost, recovery debt, nervous system load' },
  { key: 'anticipatory', label: 'Anticipatory', desc: 'future self cost, later capacity, downstream impact' },
]

export const LOAD_LABELS: { min: number; max: number; label: string }[] = [
  { min: 0, max: 1.4, label: 'barely registers' },
  { min: 1.5, max: 3.0, label: 'a quiet background thread' },
  { min: 3.1, max: 4.8, label: 'noticeable draw on presence' },
  { min: 4.9, max: 6.4, label: 'crowding other things' },
  { min: 6.5, max: 8.0, label: 'heavy foreground occupancy' },
  { min: 8.1, max: 10, label: 'near total foreground' },
]

export function getLoadLabel(total: number): string {
  const t = Math.max(0, Math.min(10, total))
  const found = LOAD_LABELS.find(l => t >= l.min && t <= l.max)
  return found?.label || 'present'
}

export function getLoadColor(total: number): string {
  if (total < 3) return '#4a6359'
  if (total < 6.5) return '#c9a227'
  return '#c17a3f'
}
